import { useEffect, useState } from "react";
import { useEventType } from "../contexts/EventTypeContext";
import { Button } from "../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../design/dialog";
import { Input } from "../design/input";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  ErrorMessage,
} from "../design/fieldset";
import { Textarea } from "../design/textarea";
import { Select } from "../design/select";
import { PlusIcon } from "@heroicons/react/20/solid";
import { Checkbox, CheckboxField } from "../design/checkbox";
import { EventType } from "../models/EventType";
import { EventCategory } from "../models/EventCategory";
import { useUser } from "../contexts/UserContext";
import { colors } from "../design/badge";

interface Props {
  eventCategoryId: EventCategory["_id"];
  eventType?: EventType;
  children?: React.ReactNode;
  className?: string;
  onEventTypeCreated?: (eventTypeId: string) => void;
}

interface Errors {
  name?: string;
}

const EventTypeButton: React.FC<Props> = ({
  eventCategoryId,
  eventType,
  children,
  className,
  onEventTypeCreated,
}) => {
  const { currentUser } = useUser();
  const { createEventType, updateEventType } = useEventType();
  const [isOpen, setIsOpen] = useState(false);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState("red");

  const [errors, setErrors] = useState<Errors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isCreatingMore, setIsCreatingMore] = useState(false);

  useEffect(() => {
    if (eventType && isOpen) {
      setName(eventType.name ?? "");
      setDescription(eventType.description ?? "");
      setColor(eventType.color ?? "red");
    }
  }, [eventType, isOpen]);

  const validateForm = () => {
    const newErrors: Errors = {};
    if (!name.trim()) newErrors.name = "Name is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const clearForm = (blockClose = false) => {
    if (!eventType) {
      setName("");
      setDescription("");
      setColor("red");
    }
    setErrors({});
    if (!blockClose) setIsOpen(false);
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsSaving(true);

    try {
      if (eventType) {
        await updateEventType(eventType._id, {
          name,
          description,
          color,
          eventCategory: eventCategoryId,
        });
      } else {
        const eventTypeId = await createEventType({
          name,
          description,
          color,
          eventCategory: eventCategoryId,
        });
        if (onEventTypeCreated) {
          onEventTypeCreated(eventTypeId);
        }
      }
      clearForm(isCreatingMore);
    } catch (e) {
      console.error(e);
      alert("Something went wrong. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const tailwindColors = [
    "red",
    "blue",
    "green",
    "yellow",
    "purple",
    "pink",
    "indigo",
    "gray",
    "black",
    "white",
  ];

  if (!currentUser || !["admin", "superadmin"].includes(currentUser?.role))
    return null;
  return (
    <>
      <Button
        type="button"
        className={className}
        onClick={() => setIsOpen(true)}
      >
        {children ?? (
          <div className="flex items-center gap-2">
            {!eventType && <PlusIcon className="w-4 h-4" />}
            <span className="hidden sm:block">
              {eventType ? "Edit Event Type" : "Create Event Type"}
            </span>
          </div>
        )}
      </Button>

      <Dialog open={isOpen} onClose={setIsOpen}>
        <DialogTitle>
          {eventType ? "Edit Event Type" : "Create Event Type"}
        </DialogTitle>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Name</Label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Name"
                />
                {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
              </Field>

              <Field>
                <Label>Color</Label>
                <Select
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                >
                  {Object.keys(colors).map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field>
                <Label>Description</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Description"
                />
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>

        <DialogActions className="justify-between">
          {!eventType && (
            <CheckboxField>
              <Checkbox
                checked={isCreatingMore}
                onChange={(checked) => setIsCreatingMore(checked)}
              />
              <Label>Create more</Label>
            </CheckboxField>
          )}
          <div className="flex gap-2">
            <Button plain onClick={() => clearForm(false)}>
              Cancel
            </Button>
            <Button disabled={isSaving} onClick={handleSubmit}>
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EventTypeButton;
